import axios from 'axios'
import { ref } from 'vue'
import { BASE_URL } from '@/environment/environment'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { toApiDate } from '@/utils/formatters'

/**
 * Компоузабл для экспорта данных (Excel/CSV) с авторизацией через Axios.
 * Получает файл в виде Blob и скачивает его без засорения DOM.
 */
export function useExportUrl(
  endpoint: string,
  filtersGetter: () => Record<string, any>,
  extraParamsGetter?: () => Record<string, any>,
  defaultFilename = 'export',
) {
  const isExporting = ref(false)
  const feedbackStore = useFeedbackStore()

  const download = async () => {
    isExporting.value = true
    try {
      const params = new URLSearchParams()
      const filters = filtersGetter()

      if (filters) {
        Object.entries(filters).forEach(([key, val]) => {
          if (val === null || val === undefined || val === '') return

          // Автоматически форматируем даты начала/конца в event_date_after/before для API
          if (key === 'created_after' || key === 'created_before') {
            const dateVal = toApiDate(val as Date | string)
            if (dateVal) {
              const apiParamKey = key === 'created_after' ? 'event_date_after' : 'event_date_before'
              params.append(apiParamKey, dateVal)
            }
            return
          }

          // Сериализуем массивы через запятую, остальные значения приводим к строке
          if (Array.isArray(val)) {
            if (val.length) params.append(key, val.join(','))
          } else {
            params.append(key, String(val))
          }
        })
      }

      // Подмешиваем дополнительные параметры (например, выбранные чекбоксами ids)
      if (extraParamsGetter) {
        const extra = extraParamsGetter()
        Object.entries(extra).forEach(([key, val]) => {
          if (val === null || val === undefined || val === '') return
          if (Array.isArray(val)) {
            if (val.length) params.append(key, val.join(','))
          } else {
            params.append(key, String(val))
          }
        })
      }

      const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`

      // Делаем запрос через Axios, чтобы автоматически прикрепился токен Bearer из интерцепторов
      const response = await axios.get(`${BASE_URL}${cleanEndpoint}`, {
        params,
        responseType: 'blob',
      })

      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const blobUrl = window.URL.createObjectURL(blob)

      // Извлечение имени файла из заголовка Content-Disposition
      const contentDisposition = response.headers['content-disposition']

      // Форматируем имя файла по умолчанию: добавляем дату и время
      const now = new Date()
      const day = String(now.getDate()).padStart(2, '0')
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const year = now.getFullYear()
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const dateStr = `${day}.${month}.${year}_${hours}-${minutes}`

      let fileName = `${defaultFilename}_${dateStr}.xlsx`

      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^; "']+)["']?/)
        if (match && match[1]) {
          fileName = decodeURIComponent(match[1])
        } else {
          const fallbackMatch = contentDisposition.match(/filename=["']?([^; "']+)["']?/)
          if (fallbackMatch && fallbackMatch[1]) {
            fileName = fallbackMatch[1]
          }
        }
      }

      // Нативное скачивание без добавления ссылки в DOM
      const link = document.createElement('a')
      link.href = blobUrl
      link.setAttribute('download', fileName)
      link.click()

      // Освобождаем объектный URL
      window.URL.revokeObjectURL(blobUrl)

      feedbackStore.showToast({
        type: 'success',
        message: 'Файл успешно экспортирован',
        time: 3000,
      })
    } catch (error) {
      console.error('❌ Ошибка скачивания файла экспорта:', error)
      feedbackStore.showToast({
        type: 'error',
        message: (error as any)?.response?.data?.detail || 'Не удалось экспортировать данные',
        time: 5000,
      })
    } finally {
      isExporting.value = false
    }
  }

  return {
    download,
    isExporting,
  }
}
